import React, { useState, useEffect, useCallback, useContext } from 'react';
import {
  Typography, Table, Tag, Select, DatePicker, Space, Input, Button, Tabs, Card, message,
} from 'antd';
import { PlusOutlined, SearchOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { formatTS } from '../utils/time';
import api from '../api';
import { UserContext } from '../App';
import ScheduleVarianceCreateModal from '../components/ScheduleVarianceCreateModal';
import {
  VARIANCE_TYPE_OPTIONS, REMOVE_POINTS_OPTIONS, VARIANCE_STATUS_OPTIONS, VARIANCE_STATUS_COLOR,
} from '../components/ScheduleVarianceFields';

const { RangePicker } = DatePicker;

let _cache = null;

export default function ScheduleVariancesPage() {
  const navigate = useNavigate();
  const currentUser = useContext(UserContext);
  const canCreate = (currentUser?.CanEditScheduleVariance ?? 0) >= 2;

  const [rows, setRows]                 = useState(() => _cache?.rows ?? []);
  // Analytics tab: Billable OT Hours by team and type, same filters as the list.
  const [otRows, setOtRows]             = useState(() => _cache?.otRows ?? []);
  const [loading, setLoading]           = useState(false);
  // No default status or team: the page opens on everything the user can see.
  const [statusFilter, setStatusFilter] = useState(() => _cache?.statusFilter ?? []);
  const [teamFilter, setTeamFilter]     = useState(() => _cache?.teamFilter ?? []);
  const [typeFilter, setTypeFilter]     = useState(() => _cache?.typeFilter ?? []);
  const [pointsFilter, setPointsFilter] = useState(() => _cache?.pointsFilter ?? '');
  const [employeeName, setEmployeeName] = useState(() => _cache?.employeeName ?? '');
  const [reporterName, setReporterName] = useState(() => _cache?.reporterName ?? '');
  const [dateRange, setDateRange]       = useState(() => _cache?.dateRange ?? [dayjs().subtract(30, 'day').startOf('month'), dayjs().add(7, 'day')]);
  const [teamOptions, setTeamOptions]   = useState([]);
  const [createOpen, setCreateOpen]     = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter.length)  params.status        = statusFilter.join(',');
      if (teamFilter.length)    params.team_id       = teamFilter.join(',');
      if (typeFilter.length)    params.variance_type = typeFilter.join(',');
      if (pointsFilter)         params.remove_points = pointsFilter;
      if (employeeName.trim())  params.employee_name = employeeName.trim();
      if (reporterName.trim())  params.reporter_name = reporterName.trim();
      if (dateRange?.[0])       params.start_date    = dateRange[0].format('YYYY-MM-DD');
      if (dateRange?.[1])       params.end_date      = dateRange[1].format('YYYY-MM-DD');
      // One search drives both tabs.
      const [res, ot] = await Promise.all([
        api.get('/api/schedule-variances', { params }),
        api.get('/api/schedule-variances/billable-ot', { params }),
      ]);
      setRows(res.data || []);
      setOtRows(ot.data || []);
      _cache = { rows: res.data || [], otRows: ot.data || [], statusFilter, teamFilter, typeFilter, pointsFilter, employeeName, reporterName, dateRange };
    } catch {
      message.error('Failed to load schedule variances.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, teamFilter, typeFilter, pointsFilter, employeeName, reporterName, dateRange]);

  // First visit loads with the default filters; coming back restores the last search.
  useEffect(() => { if (!_cache) load(); }, []); // eslint-disable-line

  useEffect(() => {
    api.get('/api/teams', { params: { no_client_users: 1 } }).then((r) => {
      const all = r.data || [];
      // Edit Manager sees every team on this log, whatever their Visible
      // Teams (the server applies the same rule).
      const isManager = (currentUser?.CanEditScheduleVariance ?? 0) >= 3;
      const vtIds = (currentUser?.VisibleTeams || []).map((t) => t.ID);
      const visible = !isManager && vtIds.length > 0 ? all.filter((t) => vtIds.includes(t.ID)) : all;
      setTeamOptions(visible.map((t) => ({ label: t.Name, value: t.ID })));
    }).catch(() => {});
  }, [currentUser]);

  // Billable OT rows (already ordered by team, then type) with a total row
  // after each team that has more than one variance type.
  const otDisplayRows = (() => {
    const out = [];
    otRows.forEach((r, i) => {
      out.push({ ...r, key: `${r.TeamName}|${r.VarianceType}` });
      const next = otRows[i + 1];
      if (!next || next.TeamName !== r.TeamName) {
        const team = otRows.filter((x) => x.TeamName === r.TeamName);
        if (team.length > 1) {
          out.push({
            key: `${r.TeamName}|total`, isTeamTotal: true, TeamName: r.TeamName,
            BillableOTHours: team.reduce((t, x) => t + Number(x.BillableOTHours || 0), 0),
          });
        }
      }
    });
    return out;
  })();

  // Every field of the rows on screen (the current search), one row each.
  const exportExcel = () => {
    const out = rows.map((r) => ({
      'ID':                r.ID,
      'Status':            r.Status,
      'Date of Variance':  r.VarianceDate ? String(r.VarianceDate).slice(0, 10) : '',
      'Employee':          r.EmployeeName || '',
      'Team':              r.TeamName || '',
      'Variance Type':     r.VarianceType || '',
      'Shift Start':       r.ShiftStart || '',
      'Shift End':         r.ShiftEnd || '',
      'Billable OT Hours': r.BillableOTHours != null ? Number(r.BillableOTHours) : '',
      'Remove Points':     r.RemovePoints || '',
      'Notes':             r.Notes || '',
      'Billing Notes':     r.BillingNotes || '',
      'Reporter':          r.ReporterName || '',
      'Created On':        r.CreatedAt ? formatTS(r.CreatedAt, 'YYYY-MM-DD HH:mm') : '',
      'Changed On':        r.UpdatedAt ? formatTS(r.UpdatedAt, 'YYYY-MM-DD HH:mm') : '',
      'Changed By':        r.UpdatedByName || '',
    }));
    const ws = XLSX.utils.json_to_sheet(out);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Schedule Variances');
    XLSX.writeFile(wb, `schedule-variances-${dayjs().format('YYYY-MM-DD')}.xlsx`);
  };

  const columns = [
    { title: 'ID', dataIndex: 'ID', key: 'ID', width: 55, sorter: (a, b) => a.ID - b.ID },
    { title: 'Date of Variance', dataIndex: 'VarianceDate', key: 'VarianceDate', width: 105,
      render: (v) => (v ? String(v).slice(0, 10) : '—'),
      sorter: (a, b) => String(a.VarianceDate).localeCompare(String(b.VarianceDate)) },
    { title: 'Employee', dataIndex: 'EmployeeName', key: 'EmployeeName', width: 150, ellipsis: true,
      sorter: (a, b) => (a.EmployeeName || '').localeCompare(b.EmployeeName || '') },
    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName', width: 120, ellipsis: true, render: (v) => v || '—' },
    // Wider and wrapping, never cut off: the type names are long.
    { title: 'Variance Type', dataIndex: 'VarianceType', key: 'VarianceType', width: 240 },
    {
      title: 'Hours Affected', key: 'Hours', width: 150,
      render: (_, r) => (r.ShiftStart || r.ShiftEnd ? `${r.ShiftStart || '?'} – ${r.ShiftEnd || '?'}` : '—'),
    },
    { title: 'Billable OT Hrs', dataIndex: 'BillableOTHours', key: 'BillableOTHours', width: 95, align: 'right',
      render: (v) => (v == null ? '—' : Number(v).toFixed(2)) },
    { title: 'Remove Points', dataIndex: 'RemovePoints', key: 'RemovePoints', width: 150 },
    {
      title: 'Status', dataIndex: 'Status', key: 'Status', width: 90,
      render: (v) => <Tag color={VARIANCE_STATUS_COLOR[v] ?? 'default'}>{v}</Tag>,
    },
    { title: 'Reporter', dataIndex: 'ReporterName', key: 'ReporterName', width: 130, ellipsis: true },
    { title: 'Created On', dataIndex: 'CreatedAt', key: 'CreatedAt', width: 140, render: (v) => formatTS(v, 'YYYY-MM-DD HH:mm') },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>Schedule Variance Log</Typography.Title>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Log Variance
          </Button>
        )}
      </div>

      <Space wrap style={{ marginBottom: 12 }}>
        <Select
          mode="multiple" allowClear placeholder="All statuses"
          options={VARIANCE_STATUS_OPTIONS} value={statusFilter} onChange={setStatusFilter}
          style={{ minWidth: 170 }}
        />
        <Select
          mode="multiple" allowClear showSearch optionFilterProp="label" placeholder="All teams"
          options={teamOptions} value={teamFilter} onChange={setTeamFilter}
          maxTagCount="responsive" style={{ minWidth: 180 }}
        />
        <Select
          mode="multiple" allowClear placeholder="All variance types"
          options={VARIANCE_TYPE_OPTIONS} value={typeFilter} onChange={setTypeFilter}
          // The list is as wide as its longest type, not the box.
          maxTagCount="responsive" style={{ minWidth: 260 }} popupMatchSelectWidth={false}
        />
        <Select
          allowClear placeholder="Remove Points"
          options={REMOVE_POINTS_OPTIONS} value={pointsFilter || undefined}
          onChange={(v) => setPointsFilter(v || '')} style={{ minWidth: 190 }}
        />
        <Input
          placeholder="Employee name" allowClear value={employeeName}
          onChange={(e) => setEmployeeName(e.target.value)} onPressEnter={load} style={{ width: 170 }}
        />
        <Input
          placeholder="Reporter name" allowClear value={reporterName}
          onChange={(e) => setReporterName(e.target.value)} onPressEnter={load} style={{ width: 170 }}
        />
        <RangePicker
          value={dateRange} onChange={setDateRange} format="YYYY-MM-DD"
          placeholder={['Variance From', 'Variance To']}
        />
        <Button type="primary" icon={<SearchOutlined />} onClick={load} loading={loading}>Search</Button>
        <Button icon={<DownloadOutlined />} onClick={exportExcel} disabled={!rows.length}>Export</Button>
      </Space>

      <Tabs
        defaultActiveKey="list"
        items={[
          {
            key: 'list',
            label: 'List of Variance Logs',
            children: (
              <Table
                rowKey="ID"
                dataSource={rows}
                columns={columns}
                loading={loading}
                size="small"
                tableLayout="fixed"
                scroll={{ x: 1425 }}
                pagination={{
                  defaultPageSize: 50,
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100'],
                  showTotal: (t) => `${t} record${t === 1 ? '' : 's'}`,
                }}
                onRow={(r) => ({ onClick: () => navigate(`/schedule-variances/${r.ID}`), style: { cursor: 'pointer' } })}
                locale={{ emptyText: 'No schedule variances found.' }}
              />
            ),
          },
          {
            key: 'analytics',
            label: 'Analytics',
            children: (
              <Card size="small" title="Billable OT Hours">
                <Table
                  rowKey="key"
                  dataSource={otDisplayRows}
                  loading={loading}
                  size="small"
                  pagination={false}
                  columns={[
                    // No sorting: it would separate team totals from their teams.
                    { title: 'Team', dataIndex: 'TeamName', key: 'TeamName',
                      render: (v, r) => (r.isTeamTotal ? <strong>{v || '—'} Total</strong> : (v || '—')) },
                    { title: 'Variance Type', dataIndex: 'VarianceType', key: 'VarianceType',
                      render: (v, r) => (r.isTeamTotal ? '' : v) },
                    { title: 'Billable OT Hours', dataIndex: 'BillableOTHours', key: 'BillableOTHours', width: 160, align: 'right',
                      render: (v, r) => (r.isTeamTotal
                        ? <strong>{Number(v || 0).toFixed(2)}</strong>
                        : Number(v || 0).toFixed(2)) },
                  ]}
                  onRow={(r) => (r.isTeamTotal ? { style: { background: '#fafafa' } } : {})}
                  summary={() => (otRows.length > 0 ? (
                    <Table.Summary.Row>
                      <Table.Summary.Cell index={0} colSpan={2}><strong>Grand Total</strong></Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <strong>{otRows.reduce((t, r) => t + Number(r.BillableOTHours || 0), 0).toFixed(2)}</strong>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  ) : null)}
                  locale={{ emptyText: 'No billable OT hours for these filters.' }}
                />
              </Card>
            ),
          },
        ]}
      />

      <ScheduleVarianceCreateModal
        open={createOpen}
        currentUser={currentUser}
        onClose={() => setCreateOpen(false)}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
    </>
  );
}
